/**
 * REQUIREMENTS SUMMARY:
 * - Parent-facing team displays should include seasons families can still act on.
 * - Current and upcoming seasons remain visible for placements, payments, and acceptance.
 * - Completed/past/archived season teams stay out of current parent dashboards.
 */

const ACTIONABLE_TEAM_SEASON_STATUS_SET = new Set<string>(['current', 'upcoming']);

export const ACTIONABLE_TEAM_SEASON_STATUSES = ['current', 'upcoming'] as const;

export type ActionableTeamSeasonStatus = typeof ACTIONABLE_TEAM_SEASON_STATUSES[number];

type SeasonQueryClient = {
  from: (table: string) => any;
};

export function isActionableTeamSeasonStatus(status?: string | null): status is ActionableTeamSeasonStatus {
  return Boolean(status && ACTIONABLE_TEAM_SEASON_STATUS_SET.has(status));
}

export function isActionableTeamSeasonId(
  seasonId: string | null | undefined,
  seasonIds: Set<string>
): boolean {
  return Boolean(seasonId && seasonIds.has(seasonId));
}

export async function fetchActionableTeamSeasonIds(
  supabaseClient: SeasonQueryClient,
  organizationId?: string
): Promise<{ seasonIds: Set<string>; error: unknown | null }> {
  if (!organizationId) {
    return { seasonIds: new Set(), error: null };
  }

  const { data, error } = await supabaseClient
    .from('seasons_computed')
    .select('id')
    .eq('organization_id', organizationId)
    .in('season_status', ACTIONABLE_TEAM_SEASON_STATUSES);

  return {
    seasonIds: new Set((data || []).map((season: { id: string }) => season.id)),
    error: error || null,
  };
}
